import { useCallback, useMemo } from 'react';
import { getRouteApi } from '@tanstack/react-router';
import { useUrlSyncedFilters } from '@/hooks/useUrlSyncedFilters';
import { useUrlSyncedSearch } from '@/hooks/useUrlSyncedSearch';
import { getMonthToDateRange } from '@/utils/date';
import { DATE_RANGE_FILTER_KEYS } from '@/utils/filterFields';
import type { ApprovalReportSearch } from '../types/marketPromotionP2.types';
import { toDonationApprovalFilterSearch, toDonationApprovalFilterValues } from '../utils/mpFilters';

const ROUTES = {
  approvalReport: getRouteApi('/_auth/market-promotion/approval-report'),
  cashCollection: getRouteApi('/_auth/market-promotion/cash-collection'),
} as const;

export type ApprovalReportRouteId = keyof typeof ROUTES;

export function useApprovalReportFilters(routeId: ApprovalReportRouteId) {
  const route = ROUTES[routeId];
  const urlSearch = route.useSearch();
  const navigate = route.useNavigate();

  const defaultPeriod = useMemo(() => getMonthToDateRange(), []);

  const search = useMemo(
    (): ApprovalReportSearch & { dateFrom: string; dateTo: string } => ({
      ...urlSearch,
      dateFrom: urlSearch.dateFrom ?? defaultPeriod.dateFrom,
      dateTo: urlSearch.dateTo ?? defaultPeriod.dateTo,
    }),
    [urlSearch, defaultPeriod],
  );

  const appliedValues = useMemo(
    () => toDonationApprovalFilterValues(search, defaultPeriod),
    [search, defaultPeriod],
  );

  const normalizeFilterValues = useCallback(
    (values: Record<string, string>) =>
      toDonationApprovalFilterValues(toDonationApprovalFilterSearch(values), defaultPeriod),
    [defaultPeriod],
  );

  const filters = useUrlSyncedFilters({
    appliedValues,
    shouldWaitForApply: true,
    defaultValues: defaultPeriod,
    rangeKeys: DATE_RANGE_FILTER_KEYS,
    normalizeValues: normalizeFilterValues,
    onApply: (values) => {
      void navigate({
        search: (previous) => ({ ...previous, ...toDonationApprovalFilterSearch(values) }),
        replace: true,
      });
    },
  });

  const searchBox = useUrlSyncedSearch({
    appliedText: urlSearch.search,
    onApply: (text) => {
      void navigate({
        search: (previous) => ({ ...previous, search: text }),
        replace: true,
      });
    },
  });

  return { ...filters, ...searchBox, search };
}
